import { useEffect, useState, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { InlineEditField } from './inline-edit-field';

export interface InlineEditProps {
  value: string;
  onSave: (next: string) => void | Promise<void>;
  /** Names the field for screen readers; the trigger has no visible label. */
  label: string;
  placeholder?: string;
  multiline?: boolean;
  /** Return a message to block the save, or null to allow it. */
  validate?: (next: string) => string | null;
  /** Render the committed value; defaults to plain text. */
  render?: (value: string) => ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * Click a field, change it in place, Enter to save, Esc to revert.
 *
 * The new value shows immediately and rolls back if the save rejects, so the
 * common case costs no waiting and the rare failure is still honest.
 */
export function InlineEdit({
  value,
  onSave,
  label,
  placeholder = 'Sin definir',
  multiline = false,
  validate,
  render,
  disabled = false,
  className,
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Once the real value catches up, the optimistic copy has nothing left to say.
  useEffect(() => {
    setOptimistic(null);
  }, [value]);

  const shown = optimistic ?? value;

  const cancel = () => {
    setEditing(false);
    setError(null);
    setDraft(value);
  };

  const commit = async () => {
    const next = draft.trim();
    if (next === value) {
      cancel();
      return;
    }

    const message = validate?.(next) ?? null;
    if (message) {
      setError(message);
      return;
    }

    setEditing(false);
    setError(null);
    setOptimistic(next);
    setSaving(true);
    try {
      await onSave(next);
    } catch (caught) {
      setOptimistic(null);
      setDraft(value);
      setError(caught instanceof Error ? caught.message : 'No pudimos guardar el cambio');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className={cn('space-y-1', className)}>
        <InlineEditField
          value={draft}
          onChange={(next) => {
            setDraft(next);
            setError(null);
          }}
          onCommit={() => void commit()}
          onCancel={cancel}
          label={label}
          placeholder={placeholder}
          multiline={multiline}
          invalid={Boolean(error)}
        />
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cn('group/inline space-y-1', className)}>
      <button
        type="button"
        disabled={disabled}
        aria-label={`Editar ${label}`}
        onClick={() => {
          setDraft(shown);
          setEditing(true);
        }}
        className={cn(
          'flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-sm transition-colors duration-150 ease-out',
          !disabled && 'hover:bg-muted',
          saving && 'text-muted-foreground',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !shown && 'text-muted-foreground')}>
          {shown ? (render?.(shown) ?? shown) : placeholder}
        </span>
        {saving ? (
          <Spinner className="size-3 shrink-0" />
        ) : (
          !disabled && (
            <Pencil
              className="size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 ease-out group-hover/inline:opacity-100"
              aria-hidden
            />
          )
        )}
      </button>

      <span aria-live="polite" className="sr-only">
        {saving ? `Guardando ${label}` : ''}
      </span>

      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
